import { defineField, defineType } from 'sanity'
import WheelDiameterSelector from '../../components/WheelDiameterSelector'

export default defineType({
  name: 'tireSize',
  title: 'Tire Sizes',
  type: 'document',
  orderings: [
    {
      title: 'Title A-Z',
      name: 'titleAsc',
      by: [{field: 'title', direction: 'asc'}],
    },
    {
      title: 'Name A-Z',
      name: 'nameAsc',
      by: [{field: 'name', direction: 'asc'}],
    },
  ],
  fields: [
    defineField({
      name: 'defaultTireFormat',
      title: 'Default Tire Format',
      type: 'string',
      options: {
        list: [
          {title: 'Standard', value: 'standard'},
          {title: 'Racing Slick', value: 'slick'},
          {title: 'Off-road', value: 'offroad'},
        ],
        layout: 'radio',
      },
      initialValue: 'standard',
      validation: (Rule) => Rule.required(),
    }),
    defineField({
      name: 'treadWidthMetric',
      title: 'Tread Width (mm)',
      type: 'number',
      hidden: ({document}) =>
        !['standard', 'slick'].includes(document?.defaultTireFormat as string),
      validation: (Rule) =>
        Rule.integer()
          .positive()
          .custom((value, context) => {
            if (
              !['standard', 'slick'].includes(
                context.document?.defaultTireFormat as string,
              )
            ) {
              return true
            }
            return value ? true : 'This field is required'
          }),
    }),
    defineField({
      name: 'aspectRatio',
      title: 'Aspect Ratio (%)',
      type: 'number',
      hidden: ({document}) => document?.defaultTireFormat !== 'standard',
      validation: (Rule) =>
        Rule.integer()
          .positive()
          .max(100)
          .custom((value, context) => {
            if (context.document?.defaultTireFormat !== 'standard') {
              return true
            }
            return value ? true : 'This field is required'
          }),
    }),
    defineField({
      name: 'tireDiameterMetric',
      title: 'Tire Diameter (mm)',
      description: 'Used for Racing Slick tire format',
      hidden: ({document}) => document?.defaultTireFormat !== 'slick',
      type: 'number',
      validation: (Rule) =>
        Rule.positive().custom((value, context) => {
          if (context.document?.defaultTireFormat !== 'slick') {
            return true
          }
          return value ? true : 'This field is required'
        }),
    }),
    defineField({
      name: 'tireDiameterImperial',
      title: 'Tire Diameter (inches)',
      description: 'Used for Off-road tire format',
      hidden: ({document}) => document?.defaultTireFormat !== 'offroad',
      type: 'number',
      validation: (Rule) =>
        Rule.positive().custom((value, context) => {
          if (context.document?.defaultTireFormat !== 'offroad') {
            return true
          }
          return value ? true : 'This field is required'
        }),
    }),
    defineField({
      name: 'treadWidthImperial',
      title: 'Tread Width (inches)',
      description: 'Used for Off-road tire format',
      hidden: ({document}) => document?.defaultTireFormat !== 'offroad',
      type: 'number',
      validation: (Rule) =>
        Rule.positive().custom((value, context) => {
          if (context.document?.defaultTireFormat !== 'offroad') {
            return true
          }
          return value ? true : 'This field is required'
        }),
    }),
    defineField({
      name: 'diameter',
      title: 'Wheel Diameter',
      type: 'reference',
      to: [{type: 'diameter'}],
      options: {
        disableNew: true,
      },
      validation: (Rule) => Rule.required(),
      components: {
        input: WheelDiameterSelector,
      },
    }),
    // Used for searchability; auto-populated on publish by setTireSizeTitleAndPublishAction
    defineField({
      name: 'title',
      title: 'Title',
      description:
        'This field is automatically populated based on the settings configured above. Values update when content is published.',
      readOnly: true,
      type: 'string',
    }),
    defineField({
      name: 'name',
      title: 'Name',
      type: 'string',
      description:
        'Still used by galleries. Set this manually when creating new tire sizes.',
    }),
  ],

  preview: {
    select: {
      format: 'defaultTireFormat',
      treadWidthMetric: 'treadWidthMetric',
      tireDiameterMetric: 'tireDiameterMetric',
      tireDiameterImperial: 'tireDiameterImperial',
      treadWidthImperial: 'treadWidthImperial',
      aspectRatio: 'aspectRatio',
      wheelDiameterRef: 'diameter.wheelDiameter',
      slug: 'slug',
    },
    prepare({
      format,
      treadWidthMetric,
      tireDiameterMetric,
      tireDiameterImperial,
      treadWidthImperial,
      aspectRatio,
      wheelDiameterRef,
    }: any) {
      let title = ''
      switch (format) {
        case 'standard':
          title = `${treadWidthMetric || ''}/${aspectRatio || ''}-${wheelDiameterRef || ''}`
          break
        case 'slick':
          title = `${treadWidthMetric || ''}/${tireDiameterMetric || ''}-${wheelDiameterRef || ''}`
          break
        case 'offroad':
          title = `${tireDiameterImperial || ''}x${treadWidthImperial || ''}R${wheelDiameterRef || ''}`
          break
        default:
          title = ''
      }

      return {
        title,
        subtitle: format ? `${format.charAt(0).toUpperCase() + format.slice(1)}` : '',
      }
    },
  },
})
